import { fetchCustomSchemas } from '@/composables/custom-data/customData';

export interface CustomContactProperty {
  name: string;
  value: string;
  type: string;
  variableName?: string;
  required: boolean;
  options?: string[];
  hasRelationship?: boolean;
  label?: string;
}

export interface CustomContactSchema {
  schemaId: string;
  schemaName: string;
  schemaDescription?: string;
  properties: CustomContactProperty[];
  requiredProperties: string[];
}

// TODO : merge that 2 functions (antho  + soufiane)

/**
 * Get custom properties related to customers for template builder
 * Includes direct fields from schemas linked to customers AND nested relations
 */
export const getCustomContactProperties = async (): Promise<CustomContactSchema[]> => {
  try {
    const fetchedSchemas = await fetchCustomSchemas();

    // Filter schemas that have a relationship with customers
    const customerRelatedSchemas = fetchedSchemas.filter((schema) => {
      if (!schema.relationships || !Array.isArray(schema.relationships)) {
        return false;
      }

      return schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts',
      );
    });

    const customSchemas: CustomContactSchema[] = [];

    // Process each schema
    customerRelatedSchemas.forEach((schema) => {
      // Find customer relation fields to exclude them
      const customerRelationFields = schema.relationships
        ?.filter((rel) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts')
        .map((rel) => rel.sourceField) || [];

      // Create properties for each field in the schema, excluding customer relation fields
      const schemaTableName = schema.table_name || schema.schema_name.toLowerCase().replace(/\s+/g, '_');
      const properties = schema.fields
        .filter((field: any) => !customerRelationFields.includes(field.name))
        .map((field: any) => {
          // Check if this field has a relationship with another custom schema
          const relationshipInfo = schema.relationships?.find(
            (rel: any) => rel.sourceField === field.name && rel.targetSchemaType === 'custom',
          );
          const hasRelationship = !!relationshipInfo;

          return {
            name: field.name,
            value: `${schema.id_custom_object}_${field.name}`,
            variableName: `contact.custom.${schemaTableName}.${field.name}`,
            type: field.type,
            required: field.required || false,
            options: field.options || undefined,
            hasRelationship,
            label: field.label || undefined,
          };
        });

      // Add properties from related custom schemas (nested relations)
      const customRelations = schema.relationships
        ?.filter((rel: any) => rel.targetSchemaType === 'custom') || [];

      customRelations.forEach((rel: any) => {
        // Find the related schema by ID, table_name, or schema_name
        const relatedSchema = fetchedSchemas.find(
          (s) => s.id_custom_object?.toString() === rel.targetSchema
            || s.table_name === rel.targetSchema
            || s.schema_name === rel.targetSchema,
        );

        if (relatedSchema && relatedSchema.fields) {
          // Get existing field names to avoid duplicates
          const existingFieldNames = properties.map((p: any) => p.name);

          // Add properties from the related schema directly (merged fields)
          // Format: contact.custom.customer_veterinaires.veterinaires.last_name
          // Skip fields that already exist in the junction table
          relatedSchema.fields.forEach((field: any) => {
            // Skip if field already exists (e.g., id_vet in both tables)
            if (existingFieldNames.includes(field.name)) {
              return;
            }

            const relatedTableName = relatedSchema.table_name || relatedSchema.schema_name.toLowerCase().replace(/\s+/g, '_');
            properties.push({
              name: field.name,
              value: `${schema.id_custom_object}_${rel.sourceField}_${field.name}`,
              variableName: `contact.custom.${schemaTableName}.${relatedTableName}.${field.name}`,
              type: field.type,
              required: false,
              options: field.options || undefined,
              hasRelationship: false,
              label: field.label || undefined,
            });
          });
        }
      });

      // Extract required property names
      const requiredProperties = properties
        .filter((prop: CustomContactProperty) => prop.required)
        .map((prop: CustomContactProperty) => prop.name);

      customSchemas.push({
        schemaId: schema.id_custom_object?.toString() || '',
        schemaName: schema.schema_name || '',
        schemaDescription: schema.description || undefined,
        properties,
        requiredProperties,
      });
    });

    return customSchemas;
  } catch (error) {
    console.error('Error fetching custom contact properties:', error);
    return [];
  }
};

export const getCustomContactPropertiesWithoutNested = async (): Promise<CustomContactSchema[]> => {
  try {
    const fetchedSchemas = await fetchCustomSchemas();

    // Filter schemas that have a relationship with customers
    const customerRelatedSchemas = fetchedSchemas.filter((schema) => {
      if (!schema.relationships || !Array.isArray(schema.relationships)) {
        return false;
      }

      return schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts',
      );
    });

    const customSchemas: CustomContactSchema[] = [];

    // Process each schema
    customerRelatedSchemas.forEach((schema) => {
      // Find customer relation fields to exclude them
      const customerRelationFields = schema.relationships
        ?.filter((rel) => rel.targetSchemaType === 'system' && rel.targetSchema === 'contacts')
        .map((rel) => rel.sourceField) || [];

      // Create properties for each field in the schema, excluding customer relation fields
      const properties = schema.fields
        .filter((field: any) => !customerRelationFields.includes(field.name))
        .map((field: any) => {
          // Check if this field has a relationship with another custom schema
          const hasRelationship = schema.relationships?.some(
            (rel: any) => rel.sourceField === field.name && rel.targetSchemaType === 'custom',
          ) || false;

          return {
            name: field.name,
            value: `${schema.id_custom_object}_${field.name}`,
            variableName: `${schema.schema_name.toLowerCase()}.${field.name}`,
            type: field.type,
            required: field.required || false,
            options: field.options || undefined,
            hasRelationship,
            label: field.label || undefined,
          };
        });

      // Extract required property names
      const requiredProperties = properties
        .filter((prop: CustomContactProperty) => prop.required)
        .map((prop: CustomContactProperty) => prop.name);

      customSchemas.push({
        schemaId: schema.id_custom_object?.toString() || '',
        schemaName: schema.schema_name || '',
        schemaDescription: schema.description || undefined,
        properties,
        requiredProperties,
      });
    });

    return customSchemas;
  } catch (error) {
    console.error('Error fetching custom contact properties:', error);
    return [];
  }
};